import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import SEOHead from "./SEOHead";
import { api } from "../services/api";
import "./Blogs.css";

const Blogs = ({ showAll = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data = await api.getArticles();
        setBlogs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading articles:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const displayBlogs = showAll ? blogs : blogs.slice(0, 3);

  const cleanExcerpt = (htmlContent, length = 110) => {
    if (!htmlContent) return "";
    const text = htmlContent.replace(/<[^>]*>?/gm, "").trim();
    return text.length > length ? text.slice(0, length) + "..." : text;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-teal" role="status">
          <span className="visually-hidden">Loading articles...</span>
        </div>
        <p className="text-muted mt-2 small">Loading health articles...</p>
      </div>
    );
  }

  if (displayBlogs.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No published articles at the moment.</p>
      </div>
    );
  }

  return (
    <section className={`blog-section section-padding ${showAll ? "page-view" : ""}`}>
      {showAll && (
        <SEOHead
          title="Orthopedic Patient Education & Health Articles | Dr. Sunil Apsingi"
          description="Read patient education guides and clinical articles by Dr. Sunil Apsingi on knee arthroscopy, ACL rehabilitation, robotic surgery, and shoulder care in Hyderabad."
          canonicalPath="/blogs"
          breadcrumbs={[
            { name: "Home", path: "/" },
            { name: "Articles", path: "/blogs" },
          ]}
        />
      )}
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-header-pill">
            <i className="fa-solid fa-book-medical me-1"></i> Patient Education
          </span>
          <h2 className="section-title-large">{showAll ? "All Clinical Articles" : "Orthopedic Insights & Updates"}</h2>
          <p className="section-subtitle-muted">
            Clinical insights and surgical guidance authored by Dr. Sunil Apsingi on joint preservation, arthroscopy, and recovery.
          </p>
        </div>

        <div className="row g-4">
          {displayBlogs.map((blog) => {
            const articleUrl = `/blogs/${blog.slug || blog.id}`;
            const publishDate = blog.datetime ? new Date(blog.datetime) : new Date();

            return (
              <div className="col-lg-4 col-md-6 d-flex align-items-stretch" key={blog.id}>
                <div className="blog-card medical-card h-100">
                  <div className="blog-img-wrapper">
                    <img
                      src={blog.poster || "/images/image-(1).png"}
                      alt={blog.title}
                      className="blog-img"
                    />
                    <div className="date-badge-modern">
                      <span className="day">{format(publishDate, "dd")}</span>
                      <span className="month">{format(publishDate, "MMM")}</span>
                    </div>
                  </div>

                  <div className="blog-body d-flex flex-column flex-grow-1 p-4">
                    <div className="blog-meta mb-2">
                      <span className="specialist-badge">{blog.specialist || "Orthopedics"}</span>
                      <span className="author-name ms-2">
                        <i className="fa-solid fa-user-doctor me-1"></i> {blog.author || "Dr. Sunil"}
                      </span>
                    </div>

                    <h4 className="blog-title mb-2">
                      <Link to={articleUrl} className="blog-link">
                        {blog.title}
                      </Link>
                    </h4>

                    <p className="blog-excerpt mb-4">
                      {cleanExcerpt(blog.content)}
                    </p>

                    <div className="mt-auto pt-3 border-top border-light d-flex justify-content-between align-items-center">
                      <Link to={articleUrl} className="read-more-link">
                        Read Article <i className="fa-solid fa-arrow-right ms-1"></i>
                      </Link>
                      <span className="text-muted small">
                        <i className="fa-regular fa-clock me-1"></i> 4 min read
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!showAll && (
          <div className="text-center mt-5">
            <Link to="/blogs" className="btn-medical-secondary">
              View All Articles <i className="fa-solid fa-arrow-right ms-2"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Blogs;